import { useTranslation } from "react-i18next"

import { DashboardDeadlineRow } from "@/components/brand/dashboard/dashboard-deadline-row"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import type { UpcomingDeadlineRow } from "@/types/brand-dashboard"

/**
 * OKS-38 — Upcoming Deadlines panel for the brand dashboard.
 *
 * `<section aria-label>` wrapping a Card with header (title + muted
 * subtitle) + body. Body branches on `rows.length`:
 *
 *   - `rows.length === 0` → muted inline message "No deadlines in
 *     the next 14 days." Panel stays mounted so layout doesn't jump
 *     when a deadline lands (UX Spec §3 lock).
 *   - `rows.length > 0` → vertical stack of `<DashboardDeadlineRow>`s,
 *     soonest-first (the controller already sorted; FE preserves order).
 *
 * Single-column layout on every breakpoint — UX Spec §5 explicitly
 * defers the right-column "Recent activity" rail to V2.
 */
export interface DashboardDeadlinesPanelProps {
  rows: UpcomingDeadlineRow[]
}

export function DashboardDeadlinesPanel({
  rows,
}: DashboardDeadlinesPanelProps) {
  const { t } = useTranslation()
  const isEmpty = rows.length === 0

  return (
    <section
      aria-label={t("brand_home.deadlines.section_aria")}
      data-testid="dashboard-deadlines-panel"
    >
      <Card className="rounded-xl border">
        <CardHeader>
          <CardTitle className="text-lg">
            {t("brand_home.deadlines.title")}
          </CardTitle>
          <p className="text-muted-foreground text-sm">
            {t("brand_home.deadlines.subtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {isEmpty ? (
            <p
              className="text-muted-foreground py-2 text-sm"
              data-testid="dashboard-deadlines-empty"
            >
              {t("brand_home.deadlines.empty")}
            </p>
          ) : (
            <ul
              className="flex flex-col gap-1"
              data-testid="dashboard-deadlines-list"
            >
              {rows.map((row) => (
                <li key={row.id}>
                  <DashboardDeadlineRow row={row} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
